import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronsUpDown, Image as ImageIcon, Link2, ListTree } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useProjects, useProject } from "@/hooks/use-projects";
import { useProjectMembers } from "@/hooks/use-project-members";
import { useProjectShares } from "@/hooks/use-share";
import { FolderTree } from "@/components/projects/folder-tree";

function initials(name: string) {
  return name.trim().slice(0, 2).toUpperCase() || "P";
}

function shareTitle(name: string | null, createdAt: string) {
  if (name && name.trim()) return name.trim();
  return new Date(createdAt).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Left project sidebar (Task 6): switcher header, Assets tree, and the Share
 * Links list. Scrolls independently of the asset grid.
 */
export function ProjectSidebar({
  projectId,
  projectsBasePath,
  foldersBasePath,
  sharesBasePath,
}: {
  projectId: string | undefined;
  projectsBasePath: string;
  foldersBasePath: string;
  sharesBasePath: string; // e.g. /admin/projects/:id/shares
}) {
  return (
    <aside className="flex h-full w-64 shrink-0 flex-col rounded-t-xl border-r border-t border-border bg-sidebar">
      <ProjectSwitcher projectId={projectId} projectsBasePath={projectsBasePath} />

      <div className="flex min-h-0 flex-1 flex-col gap-2 p-2">
        {/* Assets — top half, rounded card */}
        <div className="scrollbar-always min-h-0 flex-1 overflow-y-scroll rounded-xl border border-border px-2 py-3">
          <div className="mb-1 flex items-center gap-1.5 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <ListTree className="h-3.5 w-3.5" strokeWidth={2} />
            Assets
          </div>
          <FolderTree projectId={projectId} foldersBasePath={foldersBasePath} />
        </div>

        {/* Share Links — bottom half, rounded card (starts at the midpoint) */}
        <div className="scrollbar-always min-h-0 flex-1 overflow-y-scroll rounded-xl border border-border px-2 py-3">
          <div className="mb-1 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Share Links
          </div>
          <ShareLinksList projectId={projectId} sharesBasePath={sharesBasePath} />
        </div>
      </div>
    </aside>
  );
}

function ProjectSwitcher({
  projectId,
  projectsBasePath,
}: {
  projectId: string | undefined;
  projectsBasePath: string;
}) {
  const navigate = useNavigate();
  const { data: project } = useProject(projectId);
  const { data: projects } = useProjects();
  const { data: members } = useProjectMembers(projectId, true);

  const name = project?.name ?? "Project";
  const memberCount = members?.length ?? 0;

  return (
    <div className="border-b border-border p-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-lg px-1.5 py-1.5 text-left transition-colors hover:bg-secondary/60"
          >
            <Avatar className="h-8 w-8 rounded-lg">
              <AvatarFallback className="rounded-lg bg-primary/20 text-xs text-primary">
                {initials(name)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-foreground">{name}</div>
              {memberCount > 0 && (
                <div className="text-xs text-muted-foreground">
                  {memberCount} {memberCount === 1 ? "Member" : "Members"}
                </div>
              )}
            </div>
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={2} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-60">
          <DropdownMenuLabel>Switch project</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <div className="max-h-72 overflow-y-auto">
            {(projects ?? []).map((p) => (
              <DropdownMenuItem
                key={p.id}
                onClick={() => navigate(`${projectsBasePath}/${p.id}`)}
                className={cn(p.id === projectId && "bg-secondary")}
              >
                <Avatar className="h-5 w-5 rounded">
                  <AvatarFallback className="rounded bg-primary/20 text-[9px] text-primary">
                    {initials(p.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate">{p.name}</span>
              </DropdownMenuItem>
            ))}
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <Link to={projectsBasePath}>All Projects</Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function ShareLinksList({
  projectId,
  sharesBasePath,
}: {
  projectId: string | undefined;
  sharesBasePath: string;
}) {
  const { shareId: activeShareId } = useParams<{ shareId: string }>();
  const { data: shares, isLoading } = useProjectShares(projectId);
  const count = shares?.length ?? 0;

  return (
    <div className="space-y-0.5">
      <Link
        to={sharesBasePath}
        className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-secondary/50 hover:text-foreground"
      >
        <ImageIcon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
        <span className="flex-1 truncate">All Share Links</span>
        <span className="tabular-nums text-xs text-muted-foreground">({count})</span>
      </Link>

      {isLoading && <div className="px-2 py-1 text-xs text-muted-foreground">Loading…</div>}

      {(shares ?? []).slice(0, 25).map((s) => (
        <Link
          key={s.id}
          to={`${sharesBasePath}/${s.id}`}
          className={cn(
            "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors",
            s.id === activeShareId
              ? "bg-secondary text-foreground"
              : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground",
          )}
        >
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded bg-secondary/70">
            <Link2 className="h-3.5 w-3.5" strokeWidth={1.75} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate">{shareTitle(s.name, s.createdAt)}</span>
          </span>
        </Link>
      ))}
    </div>
  );
}
